import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, resolveMediaUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function PostDetail() {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [post, setPost] = useState(location.state?.post || null);
  const [postType, setPostType] = useState(location.state?.type || null);
  const [commentDraft, setCommentDraft] = useState('');
  const [loading, setLoading] = useState(!location.state?.post);
  const [error, setError] = useState('');

  const loadPost = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/getPosts', { params: { page: 1, limit: 50 } });
      const mediaPosts = Array.isArray(response.data) ? response.data : response.data?.posts || [];
      const foundMedia = mediaPosts.find((item) => `${item._id}` === `${postId}`);
      if (foundMedia) {
        setPost(foundMedia);
        setPostType('media');
        return;
      }
    } catch (err) {
      // ignore
    }

    try {
      const response = await api.get('/textPost/getAll');
      const textPosts = Array.isArray(response.data) ? response.data : response.data?.posts || [];
      const foundText = textPosts.find((item) => `${item._id}` === `${postId}`);
      if (foundText) {
        setPost(foundText);
        setPostType('text');
        return;
      }
      setError(t('post.notFound'));
    } catch (err) {
      setError(t('post.notFound'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!post) {
      loadPost();
    }
  }, [postId]);

  const handleComment = async () => {
    const text = commentDraft.trim();
    if (!text || postType !== 'text') return;
    try {
      const response = await api.post(`/textPost/comment/${postId}`, { text });
      const updated = response.data?.post;
      if (updated) {
        setPost(updated);
      }
      setCommentDraft('');
    } catch (err) {
      // ignore
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/gonderi/${postId}`;
    const text = postType === 'media' ? post?.description : post?.text;
    if (navigator.share) {
      await navigator.share({ title: 'Aphel', url, text: text || '' });
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
    }
  };

  if (loading) {
    return <div className="card">{t('common.loading')}</div>;
  }

  if (!post) {
    return <div className="card">{error || t('post.notFound')}</div>;
  }

  const avatarUrl = resolveMediaUrl(post.userId?.userImage);
  const mediaUrl = resolveMediaUrl(post.mediaType === 'video' ? post.video : post.image);
  const isOwner = `${post.userId?._id}` === `${user?.id}`;

  return (
    <div className="list">
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 className="section-title" style={{ marginBottom: 6 }}>{t('post.detail')}</h2>
          <div className="muted">{new Date(post.date).toLocaleString()}</div>
        </div>
        <button className="btn btn-ghost" type="button" onClick={() => navigate(-1)}>
          {t('post.back')}
        </button>
      </div>

      <div className="card list feed-card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="brand-mark" style={{ background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)' }}>
            {post.userId?.nickName?.slice(0, 1)?.toUpperCase() || 'A'}
          </div>
          {avatarUrl ? (
            <img src={avatarUrl} alt={post.userId?.nickName} style={{ width: 42, height: 42, borderRadius: 14 }} />
          ) : null}
          <div>
            <strong>{post.userId?.nickName || 'Aphel'}</strong>
            <div className="muted" style={{ fontSize: 12 }}>
              {isOwner ? t('post.you') : `@${post.userId?.nickName || ''}`}
            </div>
          </div>
        </div>

        {postType === 'media' ? (
          <>
            <p>{post.description}</p>
            <div className="media feed-media">
              {post.mediaType === 'video' ? (
                <video src={mediaUrl} controls />
              ) : (
                <img src={mediaUrl} alt="post media" />
              )}
            </div>
          </>
        ) : (
          <>
            <p>{post.text}</p>
            <h4 className="section-title" style={{ fontSize: 18, marginTop: 8 }}>
              {t('post.comments')}
            </h4>
            <div className="list">
              {(post.comments || []).map((comment) => (
                <div key={comment._id} className="comment">
                  <div className="comment-meta">@{comment.userId?.nickName || 'Aphel'}</div>
                  <div>{comment.text}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                className="input"
                placeholder={t('feed.commentPlaceholder')}
                value={commentDraft}
                onChange={(event) => setCommentDraft(event.target.value)}
              />
              <button className="btn btn-primary" type="button" onClick={handleComment}>
                {t('feed.addComment')}
              </button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="btn btn-ghost" type="button" onClick={handleShare}>
            {t('actions.share')}
          </button>
        </div>
      </div>
    </div>
  );
}
